import { Component } from '@angular/core';
import { IHeaderAngularComp } from '@ag-grid-community/angular';
import { IHeaderParams } from '@ag-grid-community/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'custom-header',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="ag-cell-label-container" (click)="onSortRequested($event)">
      <div class="ag-header-cell-label">
        <span class="ag-header-cell-text">{{ params.displayName }}</span>
        @if (params.icon) {
          <mat-icon
            class="header-icon"
            [matTooltip]="params.tooltip ?? ''"
            [matTooltipDisabled]="!params.tooltip"
            matTooltipPosition="below"
            matTooltipClass="cdw-header-tooltip"
            >{{ params.icon }}</mat-icon
          >
        }
        @if (params.enableSorting && sortActive) {
          <span class="ag-header-icon ag-sort-indicator-icon">
            @if (ascSort) {
              <span class="ag-icon ag-icon-asc" unselectable="on" role="presentation"></span>
            } @else if (descSort) {
              <span class="ag-icon ag-icon-desc" unselectable="on" role="presentation"></span>
            }
          </span>
        }
      </div>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      width: 100%;
      height: 100%;
    }

    .ag-cell-label-container {
      display: flex;
      align-items: center;
      width: 100%;
      height: 100%;
      padding: 0 12px;
      cursor: pointer;
    }

    .ag-header-cell-label {
      display: flex;
      align-items: center;
      gap: 4px;
      flex: 1;
      /* Lets .ag-header-cell-text shrink below its content width so the
         ellipsis kicks in instead of the label overflowing the cell. */
      min-width: 0;
    }

    /* flex-shrink: 0 keeps the icon at its full 16px when the sort indicator
       joins the row. Without it the icon shrinks alongside the label text and
       gets clipped by AG-Grid's overflow:hidden header wrapper (EDUS-2346 QA). */
    .header-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      flex: 0 0 auto;
      color: #6b7280;
    }

    .ag-header-cell-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }

    .ag-sort-indicator-icon {
      display: flex;
      align-items: center;
      flex: 0 0 auto;
    }

    .ag-icon-none {
      opacity: 0.5;
    }
  `]
})
export class CustomHeaderComponent implements IHeaderAngularComp {
  public params!: IHeaderParams & { icon?: string; tooltip?: string };
  public ascSort = false;
  public descSort = false;
  public sortActive = false;

  agInit(params: IHeaderParams): void {
    this.params = params as IHeaderParams & { icon?: string; tooltip?: string };
    this.updateSortState();

    // Listen for sort changes
    this.params.column.addEventListener('sortChanged', () => {
      this.updateSortState();
    });
  }

  refresh(params: IHeaderParams): boolean {
    this.params = params as IHeaderParams & { icon?: string; tooltip?: string };
    this.updateSortState();
    return true;
  }

  onSortRequested(event: MouseEvent): void {
    // Only trigger sort if sorting is enabled for this column
    if (!this.params.enableSorting) {
      return;
    }

    // Determine the sort order based on shift key (for multi-column sort)
    const isMultiSort = event.shiftKey;
    this.params.progressSort(isMultiSort);

    // Force update the sort state immediately
    setTimeout(() => this.updateSortState(), 0);
  }

  private updateSortState(): void {
    const sortModel = this.params.column.getSort();
    this.sortActive = sortModel !== null && sortModel !== undefined;
    this.ascSort = sortModel === 'asc';
    this.descSort = sortModel === 'desc';
  }
}
